/**
 * Texts of screen E10 · Emergency stop (specs/M4-emergency-stop/spec.md §4, mockup 8 `Urgence.html`).
 *
 * Provisional until Q3 (tone; French uses "tu", as in the mockups, so the spec's "vous" wording is
 * rewritten). Failure texts come from the C2 catalogue by reason code (`EMERGENCY_PAUSE_FAILED`,
 * `POSITIONS_UNAVAILABLE`), never from here (I12). Plural keys follow i18next: `_zero` is used for
 * a count of 0 in every language, then the language's own `_one` / `_other`.
 */
export const emergencyFr = {
  title: 'Arrêter toute la copie ?',
  channels_zero: 'Aucun canal à mettre en pause.',
  channels_one:
    'Ton canal sera mis en pause. Aucun nouveau signal ne sera copié tant que tu ne le réactives pas.',
  channels_other:
    'Tes {{count}} canaux seront mis en pause. Aucun nouveau signal ne sera copié tant que tu ne les réactives pas.',
  // GET /me not (yet) answered: the stop never waits for a read (FXM-02 §7).
  channelsUnknown:
    'Tous tes canaux seront mis en pause. Aucun nouveau signal ne sera copié tant que tu ne les réactives pas.',
  closeOption: {
    label: 'Clôturer aussi les positions ouvertes par FXMate',
    description: 'Elles seront clôturées au prix du marché, en gain comme en perte.',
    loading: 'Chargement de tes positions…',
  },
  noPositions: 'Aucune position FXMate ouverte.',
  // Q104 (provisional): "Retry closing" found no FXMate position left, so nothing can be sent.
  nothingLeft: 'Plus aucune position FXMate ouverte : il n’y a plus rien à clôturer.',
  manualNote: 'Les positions que tu as ouvertes toi-même dans MetaTrader ne sont pas touchées.',
  // The size is a separate text so it can be set in NUMERIC_FONT, as on E8.
  positionName: '{{side}} {{symbol}}',
  lots: '{{lots}} lot',
  side: { BUY: 'Achat', SELL: 'Vente' },
  resultUnknown: 'Résultat inconnu',
  confirm: {
    pause: 'Tout mettre en pause',
    close_one: 'Mettre en pause et clôturer {{count}} position',
    close_other: 'Mettre en pause et clôturer {{count}} positions',
  },
  stopping: 'Arrêt en cours…',
  cancel: 'Annuler',
  result: {
    title: 'Arrêt d’urgence',
    allStopped: 'Toute la copie est arrêtée.',
    // Q107 (provisional): a stop that asked to close answered PARTIAL with nothing explaining it.
    positionsNotClosed: 'Tes positions n’ont pas pu être clôturées.',
    channels: 'Canaux',
    positions: 'Positions',
    paused: 'En pause',
    retry: 'Réessayer',
    status: {
      CLOSED: 'Clôturée',
      ALREADY_CLOSED: 'Déjà clôturée',
      // No automatic retry is claimed: the engine does not retry closes yet (Q40, Q104).
      CLOSING: 'Clôture en cours.',
      // Q104 (provisional): no retry is claimed, the engine does not retry yet (Q40).
      CLOSE_FAILED: 'Non clôturée.',
      PENDING_ACCOUNT: 'Compte injoignable : la position sera clôturée dès son retour.',
      // Q104 (provisional): a later GET /positions no longer lists it; how it ended is unknown.
      NO_LONGER_OPEN: 'Plus ouverte.',
    },
    retryClose: 'Relancer la clôture',
    followOnPositions: 'Suivre sur Positions',
    backHome: 'Retour à l’accueil',
  },
};

/** Shape of the `emergency` section of `Messages`. */
export type EmergencyMessages = typeof emergencyFr;

/** English texts of screen E10. `EmergencyMessages` makes a missing or extra key a compile error. */
export const emergencyEn: EmergencyMessages = {
  title: 'Stop all copying?',
  channels_zero: 'No channel to pause.',
  channels_one:
    'Your channel will be paused. No new signal will be copied until you turn it back on.',
  channels_other:
    'Your {{count}} channels will be paused. No new signal will be copied until you turn them back on.',
  channelsUnknown:
    'All your channels will be paused. No new signal will be copied until you turn them back on.',
  closeOption: {
    label: 'Also close positions opened by FXMate',
    description: 'They will be closed at market price, in profit or at a loss.',
    loading: 'Loading your positions…',
  },
  noPositions: 'No open FXMate position.',
  nothingLeft: 'No FXMate position is open anymore: there is nothing left to close.',
  manualNote: 'Positions you opened yourself in MetaTrader are not affected.',
  positionName: '{{side}} {{symbol}}',
  lots: '{{lots}} lot',
  side: { BUY: 'Buy', SELL: 'Sell' },
  resultUnknown: 'Result unknown',
  confirm: {
    pause: 'Pause everything',
    close_one: 'Pause and close {{count}} position',
    close_other: 'Pause and close {{count}} positions',
  },
  stopping: 'Stopping…',
  cancel: 'Cancel',
  result: {
    title: 'Emergency stop',
    allStopped: 'All copying is stopped.',
    positionsNotClosed: 'Your positions could not be closed.',
    channels: 'Channels',
    positions: 'Positions',
    paused: 'Paused',
    retry: 'Retry',
    status: {
      CLOSED: 'Closed',
      ALREADY_CLOSED: 'Already closed',
      CLOSING: 'Closing in progress.',
      CLOSE_FAILED: 'Not closed.',
      PENDING_ACCOUNT: 'Account unreachable: the position will close as soon as it is back.',
      NO_LONGER_OPEN: 'No longer open.',
    },
    retryClose: 'Retry closing',
    followOnPositions: 'Follow on Positions',
    backHome: 'Back to Home',
  },
};
